import {
  AuthContext,
  useI18n,
  usePastDiagnosis,
  useUhOhStatus,
  useUserProfile,
} from 'contexts/AuthContext'
import { useMetadataOptions } from 'hooks/careHooks'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import ContentHeader from '../ContentHeader'
import CareSelectButton from 'components/CareSelectButton/index'
import { ScreenKey } from '../util'
import CareInput from 'components/CareInput/index'
import { Button } from 'antd'
import { isMobile } from 'utils/basic'
import _ from 'lodash'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { setBrazeUserAttribute } from 'utils/brazeInit'
import { updateUserPromise } from 'apis/FitOnCareAPI'
import { useUserProfileStatus } from 'hooks/useUserProfileStatus'
import useUpdateQuickNotes from 'hooks/useUpdateQuickNotes'
import { getLocalLanguage } from 'utils/authLocalStorage'
import { UhOhStatus } from '../UhOh'
export const isStartsWithWlc = (item) => {
  return item
    ?.replace(/<[^>]*>/g, '')
    ?.trim()
    ?.startsWith('wlc')
}

const PastDiagnosis = (props) => {
  const { i18n } = useI18n()
  const { pastDiagnosis, setPastDiagnosis } = usePastDiagnosis()
  const { pastDiagnosisOptions } = useMetadataOptions()
  const { sku, shouldSkipScheduledTime } = props
  const { shouldSkipFlow } = useUserProfileStatus()
  const { updateQuickNotes } = useUpdateQuickNotes()
  const { setUhOhStatus } = useUhOhStatus()
  const authContext = useContext(AuthContext)
  const { userProfile } = useUserProfile()

  useEffect(() => {
    logAmplitudeEvent('Screen View: Onboarding - Condition Primary', {
      sku,
      language: getLocalLanguage(),
    })
  }, [sku])

  const isSelected = (id) => {
    return pastDiagnosis.some((item) => item.id === id)
  }

  const setSelected = (item) => {
    const isExist = isSelected(item.id)
    if (isExist) {
      setPastDiagnosis(pastDiagnosis.filter((i) => i.id !== item.id))
    } else {
      if (['Other', 'None of the above'].includes(item.name)) {
        setPastDiagnosis([item])
      } else {
        const newPastDiagnosis = pastDiagnosis.filter(
          (i) => !['Other', 'None of the above'].includes(i.name)
        )
        setPastDiagnosis([...newPastDiagnosis, item])
      }
    }
  }

  const isOtherSelected = useMemo(() => {
    const otherItem = pastDiagnosis.find((item) => item.name === 'Other')
    return { isSelected: !!otherItem, item: otherItem }
  }, [pastDiagnosis])

  const isNextButtonEnable = useMemo(() => {
    return (
      !_.isEmpty(pastDiagnosis) &&
      (!isOtherSelected.isSelected ||
        !_.isEmpty(isOtherSelected.item.otherText))
    )
  }, [pastDiagnosis, isOtherSelected])

  const isNotAvailable = useMemo(() => {
    return pastDiagnosis.length === 1 && !pastDiagnosis[0].available
  }, [pastDiagnosis])

  const openNextScreen = useCallback(() => {
    if (isNotAvailable) {
      setUhOhStatus(UhOhStatus.PAST_DIAGNOSIS)
      props.setCurrentKey(ScreenKey.UH_OH)
    } else {
      props.setCurrentKey(
        shouldSkipFlow
          ? shouldSkipScheduledTime
            ? ScreenKey.SKU_CONGRATULATIONS
            : ScreenKey.SCHEDULED_TIME
          : ScreenKey.TALK_ABOUT_COVERAGE
      )
    }
  }, [
    pastDiagnosis,
    props,
    shouldSkipFlow,
    shouldSkipScheduledTime,
    isNotAvailable,
  ])

  const onSubmit = () => {
    const condition =
      pastDiagnosis[0].name === 'Other'
        ? [`Other - ${isOtherSelected.item.otherText}`]
        : pastDiagnosis[0].name === 'None of the above'
          ? ['None of the above']
          : pastDiagnosis.map((item) => item.name)

    logAmplitudeEvent('Click: Onboarding - Condition Primary', {
      sku,
      Condition: condition,
      language: getLocalLanguage(),
    })

    setBrazeUserAttribute(
      'wlc_condition',
      pastDiagnosis.map((item) => item.name)
    )

    updateHealthieClient()
    updateUserSkuCondition()

    openNextScreen()
  }

  const updateHealthieClient = async () => {
    const quickNotes = `wlc:[${pastDiagnosis
      .map((item) =>
        item.name === 'Other' ? `Other - ${item.otherText}` : item.name
      )
      .join(', ')}]`
    updateQuickNotes(quickNotes, 'wlc')
  }

  const updateUserSkuCondition = async () => {
    const body = {
      skuCondition: {
        sku,
        skuPrimary: pastDiagnosis.map((item) => item.id?.toString()),
        skuPrimaryOther: isOtherSelected.isSelected
          ? [isOtherSelected.item.otherText]
          : [],
      },
      ...(!isNotAvailable && {
        skuStatus: {
          sku,
          status: 1,
        },
      }),
    }
    updateUserPromise(body, authContext)
  }

  return (
    <div className='info-sec'>
      <ContentHeader {...props} />
      <div className='info-content'>
        <div className='title'>
          {i18n(
            'Have you now or in the past had any of the following medical conditions?'
          )}
        </div>
        <div className='subtitle' style={{ marginBottom: '32px' }}>
          {i18n(
            'To be eligible for this program, you must have a qualifying medical condition'
          )}
        </div>
        {pastDiagnosisOptions.map((item, index) => (
          <div key={index}>
            <CareSelectButton
              key={index}
              {...{
                selected: isSelected(item.id),
                setSelected: () => {
                  setSelected(item)
                },
                text: item.name,
                className: 'm-b-12 left-align',
              }}
            />
          </div>
        ))}
        {isOtherSelected.isSelected && (
          <CareInput
            {...{
              inputValue: isOtherSelected.item.otherText || '',
              setInputValue: (value) => {
                setPastDiagnosis([
                  { ...isOtherSelected.item, otherText: value },
                ])
              },
              placeholder: i18n('Please specify'),
              focus: isOtherSelected.isSelected,
              maxLength: 50,
              className: 'input-sec-padding',
            }}
          />
        )}
        <Button
          className='btn-primary full-width-btn'
          style={isMobile() ? { width: '100%' } : {}}
          onClick={onSubmit}
          disabled={!isNextButtonEnable}
        >
          {i18n('Next')}
        </Button>
      </div>
    </div>
  )
}

export default PastDiagnosis
